import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "@/components/ui/use-toast";
import {
  Link2,
  Plus,
  Copy,
  Users,
  Eye,
  Clock,
  MousePointerClick,
  ArrowUpDown,
  Loader2,
  Trash2,
  ChevronDown,
} from "lucide-react";

interface TrackedClient {
  id: string;
  ref_code: string;
  client_name: string;
  client_email: string | null;
  notes: string | null;
  created_at: string;
}

interface ActivityEvent {
  id: string;
  ref_code: string;
  page_url: string;
  event_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export function LeadTracker() {
  const [clients, setClients] = useState<TrackedClient[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [creating, setCreating] = useState(false);

  // Auto-generate slug from client name
  const autoSlug = newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const displaySlug = autoSlug || "client-name";

  const siteUrl = "https://minzo.art";
  const displayUrl = "minzo.art";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    const [clientsRes, activityRes] = await Promise.all([
      supabase
        .from("tracked_clients" as any)
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("client_activity" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500),
    ]);

    if (clientsRes.data) setClients(clientsRes.data as unknown as TrackedClient[]);
    if (activityRes.data) setActivity(activityRes.data as unknown as ActivityEvent[]);
    setIsLoading(false);
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);

    const slug = newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const insertPayload: any = { client_name: newName.trim(), client_email: newEmail.trim() || null, notes: newNotes.trim() || null, ref_code: slug };
    const { data, error } = await (supabase
      .from("tracked_clients" as any) as any)
      .insert(insertPayload)
      .select()
      .single();

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else if (data) {
      setClients((prev) => [data as TrackedClient, ...prev]);
      toast({ title: "Link created", description: `Tracking link for ${newName} is ready` });
      setNewName("");
      setNewEmail("");
      setNewNotes("");
      setDialogOpen(false);
    }
    setCreating(false);
  };

  const handleDelete = async (client: TrackedClient) => {
    if (!confirm(`Delete tracking link for "${client.client_name}"? All activity data will be removed.`)) return;

    // Delete activity first, then client
    await (supabase.from("client_activity" as any) as any).delete().eq("ref_code", client.ref_code);
    const { error } = await (supabase.from("tracked_clients" as any) as any).delete().eq("id", client.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      setActivity((prev) => prev.filter((a) => a.ref_code !== client.ref_code));
      toast({ title: "Deleted", description: `Tracking link for ${client.client_name} removed` });
    }
  };

  const copyLink = (refCode: string) => {
    navigator.clipboard.writeText(`${siteUrl}/${refCode}`);
    toast({ title: "Copied!", description: "Tracking link copied to clipboard" });
  };

  // Group activity by client
  const clientActivity = useMemo(() => {
    const map = new Map<string, ActivityEvent[]>();
    activity.forEach((a) => {
      const list = map.get(a.ref_code) || [];
      list.push(a);
      map.set(a.ref_code, list);
    });
    return map;
  }, [activity]);

  // Group events into sessions (30-min gap = new session)
  const SESSION_GAP_MS = 30 * 60 * 1000;
  const getClientSessions = (refCode: string) => {
    const events = clientActivity.get(refCode) || [];
    // Sort oldest first for session grouping
    const sorted = [...events].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    const sessions: { start: Date; end: Date; pages: { url: string; timeSpent: number; scrollDepth: number; viewedAt: Date }[]; ctaClicks: { text: string; href: string; at: Date }[] }[] = [];
    let current: typeof sessions[0] | null = null;

    sorted.forEach((ev) => {
      const t = new Date(ev.created_at);
      if (!current || t.getTime() - current.end.getTime() > SESSION_GAP_MS) {
        current = { start: t, end: t, pages: [], ctaClicks: [] };
        sessions.push(current);
      }
      current.end = t;

      if (ev.event_type === "page_view") {
        current.pages.push({ url: ev.page_url, timeSpent: 0, scrollDepth: 0, viewedAt: t });
      } else if (ev.event_type === "page_exit") {
        // Match to the last page_view with same URL
        const lastPage = [...current.pages].reverse().find((p) => p.url === ev.page_url && p.timeSpent === 0);
        if (lastPage) {
          lastPage.timeSpent = (ev.metadata?.time_spent_seconds as number) || 0;
          lastPage.scrollDepth = (ev.metadata?.max_scroll_percent as number) || 0;
        }
      } else if (ev.event_type === "cta_click") {
        current.ctaClicks.push({
          text: (ev.metadata?.button_text as string) || "",
          href: (ev.metadata?.href as string) || "",
          at: t,
        });
      }
    });

    // Reverse so newest session first
    return sessions.reverse();
  };

  // Compute stats per client
  const clientStats = useMemo(() => {
    const stats = new Map<
      string,
      { pageViews: number; totalTime: number; ctaClicks: number; lastVisit: string | null; topPages: string[] }
    >();

    clients.forEach((c) => {
      const events = clientActivity.get(c.ref_code) || [];
      const pageViews = events.filter((e) => e.event_type === "page_view").length;
      const ctaClicks = events.filter((e) => e.event_type === "cta_click").length;
      const totalTime = events
        .filter((e) => e.event_type === "page_exit")
        .reduce((sum, e) => sum + ((e.metadata?.time_spent_seconds as number) || 0), 0);
      const lastVisit = events.length > 0 ? events[0].created_at : null;

      // Top pages by view count
      const pageCounts = new Map<string, number>();
      events
        .filter((e) => e.event_type === "page_view")
        .forEach((e) => pageCounts.set(e.page_url, (pageCounts.get(e.page_url) || 0) + 1));
      const topPages = [...pageCounts.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([url]) => url);

      stats.set(c.ref_code, { pageViews, totalTime, ctaClicks, lastVisit, topPages });
    });

    return stats;
  }, [clients, clientActivity]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-playfair text-2xl font-semibold text-foreground">Lead Tracker</h2>
          <p className="text-sm text-muted-foreground">
            Create unique links and track client browsing activity
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" /> New Link
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Tracking Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label htmlFor="client-name">Client Name *</Label>
                <Input
                  id="client-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. Studio ABC"
                />
              </div>
              {newName.trim() && (
                <div className="rounded-md border border-dashed p-3">
                  <p className="text-xs text-muted-foreground mb-1">Generated Link</p>
                  <p className="font-mono text-sm text-foreground break-all">{displayUrl}/{displaySlug}</p>
                </div>
              )}
              <div>
                <Label htmlFor="client-email">Email (optional)</Label>
                <Input
                  id="client-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="contact@studio.com"
                />
              </div>
              <div>
                <Label htmlFor="client-notes">Notes (optional)</Label>
                <Input
                  id="client-notes"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder="Met at trade show"
                />
              </div>
              <Button onClick={handleCreate} disabled={!newName.trim() || creating} className="w-full">
                {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Generate Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Users className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">{clients.length}</p>
              <p className="text-xs text-muted-foreground">Tracked Links</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Eye className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">
                {activity.filter((a) => a.event_type === "page_view").length}
              </p>
              <p className="text-xs text-muted-foreground">Total Views</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <MousePointerClick className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">
                {activity.filter((a) => a.event_type === "cta_click").length}
              </p>
              <p className="text-xs text-muted-foreground">CTA Clicks</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Link2 className="h-5 w-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">
                {clients.filter((c) => (clientActivity.get(c.ref_code)?.length || 0) > 0).length}
              </p>
              <p className="text-xs text-muted-foreground">Active Clients</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client list */}
      {clients.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Link2 className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="text-lg font-medium">No tracking links yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Create a link to share with a potential client
            </p>
          </CardContent>
        </Card>
      ) : (
        <Accordion type="single" collapsible className="space-y-3">
          {clients.map((client) => {
            const stats = clientStats.get(client.ref_code);
            const events = clientActivity.get(client.ref_code) || [];
            const hasActivity = events.length > 0;

            return (
              <AccordionItem key={client.id} value={client.id} className="rounded-lg border bg-card">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex flex-1 items-center justify-between pr-4">
                    <div className="flex items-center gap-3 text-left">
                      <div>
                        <p className="font-medium text-foreground">{client.client_name}</p>
                        {client.client_email && (
                          <p className="text-xs text-muted-foreground">{client.client_email}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {hasActivity ? (
                        <Badge variant="default" className="text-xs">
                          {stats?.pageViews || 0} views
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          No visits
                        </Badge>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  <div className="space-y-4">
                    {/* Link */}
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={`${displayUrl}/${client.ref_code}`}
                        className="text-xs font-mono"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyLink(client.ref_code)}
                        className="shrink-0 gap-1"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(client)}
                        className="shrink-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    {client.notes && (
                      <p className="text-xs text-muted-foreground italic">{client.notes}</p>
                    )}

                    {hasActivity ? (
                      <>
                        {/* Summary stats */}
                        <div className="grid grid-cols-3 gap-3">
                          <div className="rounded-md border p-3 text-center">
                            <Eye className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                            <p className="text-lg font-semibold">{stats?.pageViews}</p>
                            <p className="text-[10px] text-muted-foreground">Page Views</p>
                          </div>
                          <div className="rounded-md border p-3 text-center">
                            <Clock className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                            <p className="text-lg font-semibold">{formatTime(stats?.totalTime || 0)}</p>
                            <p className="text-[10px] text-muted-foreground">Total Time</p>
                          </div>
                          <div className="rounded-md border p-3 text-center">
                            <MousePointerClick className="mx-auto mb-1 h-4 w-4 text-muted-foreground" />
                            <p className="text-lg font-semibold">{stats?.ctaClicks}</p>
                            <p className="text-[10px] text-muted-foreground">CTA Clicks</p>
                          </div>
                        </div>

                        <Separator />

                        {/* Sessions */}
                        <div>
                          <p className="mb-3 text-xs font-medium text-muted-foreground">
                            Visit Sessions ({getClientSessions(client.ref_code).length})
                          </p>
                          <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {getClientSessions(client.ref_code).map((session, idx) => {
                              const sessions = getClientSessions(client.ref_code);
                              const sessionTime = session.pages.reduce((s, p) => s + p.timeSpent, 0);
                              const pageNames: Record<string, string> = {
                                "/": "Home",
                                "/cases": "Cases",
                                "/materials": "Materials",
                                "/components": "Components",
                                "/concierge": "Concierge",
                                "/projects": "Projects",
                                "/favorites": "Favorites",
                              };
                              const getPageLabel = (url: string) => {
                                if (pageNames[url]) return pageNames[url];
                                if (url.startsWith("/cases/")) return url.replace("/cases/", "Case: ").replace(/-/g, " ");
                                return url;
                              };
                              // Deduplicate consecutive same-page views
                              const dedupedPages = session.pages.reduce<typeof session.pages>((acc, page) => {
                                const last = acc[acc.length - 1];
                                if (last && last.url === page.url) {
                                  last.timeSpent += page.timeSpent;
                                  last.scrollDepth = Math.max(last.scrollDepth, page.scrollDepth);
                                } else {
                                  acc.push({ ...page });
                                }
                                return acc;
                              }, []);

                              return (
                                <Collapsible key={idx} defaultOpen={idx === 0}>
                                  <div className="rounded-lg border bg-card overflow-hidden">
                                    {/* Clickable session header */}
                                    <CollapsibleTrigger className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors group">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary text-[11px] font-bold">
                                          {sessions.length - idx}
                                        </span>
                                        <div className="text-left">
                                          <p className="text-xs font-medium text-foreground">
                                            {session.start.toLocaleDateString(undefined, {
                                              weekday: "short", month: "short", day: "numeric",
                                            })}
                                          </p>
                                          <p className="text-[10px] text-muted-foreground">
                                            {session.start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                            {" — "}
                                            {session.end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <div className="text-right">
                                          <p className="text-sm font-semibold text-foreground">{formatTime(sessionTime)}</p>
                                          <p className="text-[10px] text-muted-foreground">{dedupedPages.length} page{dedupedPages.length !== 1 ? "s" : ""}</p>
                                        </div>
                                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                                      </div>
                                    </CollapsibleTrigger>

                                    <CollapsibleContent>
                                      <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border/50">
                                        {/* Page journey */}
                                        <div className="space-y-0">
                                          {dedupedPages.map((page, pi) => (
                                            <div key={pi} className="flex items-stretch gap-3">
                                              <div className="flex flex-col items-center w-3">
                                                <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${pi === 0 ? "bg-primary" : "bg-muted-foreground/30"}`} />
                                                {pi < dedupedPages.length - 1 && <div className="w-px flex-1 bg-border" />}
                                              </div>
                                              <div className="flex-1 flex items-center justify-between py-1.5 min-w-0">
                                                <span className="text-xs text-foreground truncate">{getPageLabel(page.url)}</span>
                                                <div className="flex items-center gap-2 text-[11px] text-muted-foreground shrink-0 ml-2">
                                                  {page.timeSpent > 0 && <span>{formatTime(page.timeSpent)}</span>}
                                                  {page.scrollDepth > 0 && <span className="text-[10px]">↓{page.scrollDepth}%</span>}
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>

                                        {/* CTA clicks */}
                                        {session.ctaClicks.length > 0 && (
                                          <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border/50">
                                            {session.ctaClicks.map((cta, ci) => (
                                              <Badge key={ci} variant="default" className="text-[10px] gap-1">
                                                <MousePointerClick className="h-2.5 w-2.5" />
                                                {cta.text || "Link click"}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No activity yet — share the link with this client
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </div>
  );
}

function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return `${mins}m ${secs}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}
